import { useEffect, useState } from "react";
import { Star } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";

const listeners = new Set();
let cache = null;
const load = () => api.get("/member/favourites").then(({ data }) => { cache = new Set(data.ids); listeners.forEach((fn) => fn(cache)); });

export function useFavourites() {
  const { user } = useAuth();
  const isMember = user?.role === "client" && user?.account_kind === "member";
  const [ids, setIds] = useState(cache || new Set());
  useEffect(() => {
    if (!isMember) return;
    listeners.add(setIds);
    if (!cache) load().catch(() => {});
    else setIds(cache);
    return () => listeners.delete(setIds);
  }, [isMember]);
  const toggle = async (trainerId, brandName) => {
    const on = ids.has(trainerId);
    await (on ? api.delete(`/member/favourites/${trainerId}`) : api.put(`/member/favourites/${trainerId}`));
    toast.success(on ? `Removed ${brandName || "business"} from favourites` : `Starred ${brandName || "business"} — we'll email you about new classes`);
    await load();
  };
  return { isMember, ids, toggle, reload: load };
}

export function FavouriteStar({ trainerId, brandName, size = 16, className = "", accent }) {
  const { isMember, ids, toggle } = useFavourites();
  if (!isMember || !trainerId) return null;
  const on = ids.has(trainerId);
  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); e.preventDefault(); toggle(trainerId, brandName).catch(() => toast.error("Couldn't update favourite")); }}
      className={`inline-flex items-center justify-center transition-transform hover:scale-110 ${on ? "text-amber-400" : "text-zinc-500 hover:text-zinc-200"} ${className}`}
      style={on && accent ? { color: accent } : undefined}
      title={on ? "Remove from favourites" : "Star this business — get emails about new classes and open spots"}
      aria-pressed={on}
      data-testid={`favourite-star-${trainerId}`}
    >
      <Star size={size} weight={on ? "fill" : "regular"} />
    </button>
  );
}
